import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Page,
  Layout,
  Card,
  Text,
  TextField,
  Banner,
  Toast,
  BlockStack,
  InlineStack,
  Button,
  Checkbox,
  Spinner,
  Modal,
  DropZone,
  Badge,
  Icon,
} from "@shopify/polaris";
import { DeleteIcon } from "@shopify/polaris-icons";
import {
  usePurchaseOrders,
  type PurchaseOrder,
} from "../hooks/usePurchaseOrders";
import {
  useInvoiceProcessing,
  type MatchedLineItem,
  type MatchClassification,
} from "../hooks/useInvoiceProcessing";
import { formatCurrency } from "../utils/cost-calculations";

// ============================================================
// Row color scheme matching legacy Access system
// ============================================================
const ROW_COLORS: Record<MatchClassification, { bg: string; label: string }> = {
  green:  { bg: "#E8F5E9", label: "Received OK" },
  yellow: { bg: "#FFF9C4", label: "Cost Change" },
  red:    { bg: "#FCE4EC", label: "Short Shipped" },
  blue:   { bg: "#E3F2FD", label: "New Product" },
};

interface ReceivingRow {
  poLineItemId: string | null;
  received: boolean;
  quantityReceived: string;
  quantityDamaged: string;
  invoicedCaseCost: string;
  newRetailPrice: string;
  printLabel: boolean;
  notes: string;
  classification: MatchClassification | null;
}

export default function Receiving() {
  const { poId } = useParams<{ poId: string }>();
  const navigate = useNavigate();
  const { loadPurchaseOrder, receivePurchaseOrder, error: poError } = usePurchaseOrders();
  const {
    uploading,
    processing,
    loadingResults,
    error: invoiceError,
    uploadedInvoices,
    processResult,
    hasSavedResults,
    loadInvoices,
    loadSavedResults,
    uploadInvoices,
    processInvoices,
    deleteInvoice,
    clearResults,
    clearSavedResults,
  } = useInvoiceProcessing();

  const [po, setPO] = useState<PurchaseOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [shopifyWarning, setShopifyWarning] = useState<string | null>(null);

  // Form fields
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [receivingNotes, setReceivingNotes] = useState("");
  const [rows, setRows] = useState<Map<string, ReceivingRow>>(new Map());

  // Track whether invoice processing has been applied
  const [invoiceProcessed, setInvoiceProcessed] = useState(false);

  // Load PO, invoices, and any saved results
  useEffect(() => {
    if (poId) {
      setLoading(true);
      Promise.all([
        loadPurchaseOrder(poId),
        loadInvoices(poId),
        loadSavedResults(poId),
      ]).then(([data]) => {
        setPO(data);
        if (data?.lineItems) {
          const map = new Map<string, ReceivingRow>();
          for (const li of data.lineItems) {
            const outstanding = li.quantityOrdered - li.quantityReceived;
            map.set(li.id, {
              poLineItemId: li.id,
              received: outstanding > 0,
              quantityReceived: outstanding > 0 ? String(outstanding) : "0",
              quantityDamaged: "0",
              invoicedCaseCost: "",
              newRetailPrice: li.retailPrice ? String(Number(li.retailPrice)) : "",
              printLabel: false,
              notes: "",
              classification: null,
            });
          }
          setRows(map);
        }
        setLoading(false);
      });
    }
  }, [poId]);

  // When invoice processing completes, apply results to the table
  useEffect(() => {
    if (!processResult || !po?.lineItems) return;

    const map = new Map<string, ReceivingRow>();
    const { matchedItems } = processResult;

    // First: apply matched PO line items
    for (const item of matchedItems) {
      if (item.poLineItemId) {
        // Invoice qty is in units — convert to cases for PO tracking
        const caseQty = item.packSize > 1
          ? Math.round(item.invoiceQty / item.packSize)
          : item.invoiceQty;
        // Find the PO line item to get current retail price
        const poLi = po.lineItems.find((l) => l.id === item.poLineItemId);
        const currentRetail = poLi?.retailPrice ? String(Number(poLi.retailPrice)) : "";

        map.set(item.poLineItemId, {
          poLineItemId: item.poLineItemId,
          received: item.classification !== "red",
          quantityReceived: String(caseQty),
          quantityDamaged: "0",
          invoicedCaseCost: item.invoiceCaseCost != null ? String(item.invoiceCaseCost) : "",
          newRetailPrice: currentRetail,
          printLabel: item.classification === "yellow", // auto-check for cost changes
          notes: item.classification === "yellow"
            ? `Cost changed from ${formatCurrency(item.caseCost - item.offInvoice)} to ${formatCurrency(item.invoiceCaseCost!)}`
            : "",
          classification: item.classification,
        });
      }
    }

    // Ensure all PO line items are in the map (some may not have been matched)
    for (const li of po.lineItems) {
      if (!map.has(li.id)) {
        map.set(li.id, {
          poLineItemId: li.id,
          received: false,
          quantityReceived: "0",
          quantityDamaged: "0",
          invoicedCaseCost: "",
          newRetailPrice: li.retailPrice ? String(Number(li.retailPrice)) : "",
          printLabel: false,
          notes: "",
          classification: "red",
        });
      }
    }

    setRows(map);
    setInvoiceProcessed(true);

    // Set invoice number and date from parsed invoice metadata
    if (processResult.invoiceMeta && processResult.invoiceMeta.length > 0) {
      const meta = processResult.invoiceMeta[0];
      if (meta.invoiceNumber) setInvoiceNumber(meta.invoiceNumber);
      if (meta.invoiceDate) setInvoiceDate(meta.invoiceDate);
    } else if (matchedItems.length > 0 && matchedItems[0].invoiceNumber) {
      setInvoiceNumber(matchedItems[0].invoiceNumber);
    }

    setToastMessage(`Processed! ${processResult.summary.greenCount} matched, ${processResult.summary.yellowCount} cost changes, ${processResult.summary.redCount} short shipped, ${processResult.summary.blueCount} new`);
  }, [processResult, po]);

  const handleRowChange = useCallback(
    (lineItemId: string, field: keyof ReceivingRow, value: string | boolean) => {
      setRows((prev) => {
        const next = new Map(prev);
        const row = { ...next.get(lineItemId)! };
        if (field === "printLabel") {
          row.printLabel = value === "true" || value === true;
        } else {
          (row as any)[field] = value;
        }
        next.set(lineItemId, row);
        return next;
      });
    },
    []
  );

  const handleToggleReceived = useCallback(
    (lineItemId: string, outstanding: number) => {
      setRows((prev) => {
        const next = new Map(prev);
        const row = { ...next.get(lineItemId)! };
        row.received = !row.received;
        if (!invoiceProcessed) {
          row.quantityReceived = row.received ? String(outstanding) : "0";
        }
        next.set(lineItemId, row);
        return next;
      });
    },
    [invoiceProcessed]
  );

  const handleCheckAll = useCallback(() => {
    if (!po?.lineItems) return;
    setRows((prev) => {
      const next = new Map(prev);
      for (const li of po.lineItems!) {
        const outstanding = li.quantityOrdered - li.quantityReceived;
        const row = { ...next.get(li.id)! };
        row.received = true;
        if (!invoiceProcessed) {
          row.quantityReceived = String(outstanding);
        }
        next.set(li.id, row);
      }
      return next;
    });
  }, [po, invoiceProcessed]);

  const handleUncheckAll = useCallback(() => {
    if (!po?.lineItems) return;
    setRows((prev) => {
      const next = new Map(prev);
      for (const li of po.lineItems!) {
        const row = { ...next.get(li.id)! };
        row.received = false;
        if (!invoiceProcessed) {
          row.quantityReceived = "0";
        }
        next.set(li.id, row);
      }
      return next;
    });
  }, [po, invoiceProcessed]);

  // File upload handler
  const handleDropZoneDrop = useCallback(
    (_dropFiles: File[], acceptedFiles: File[]) => {
      if (poId && acceptedFiles.length > 0) {
        uploadInvoices(poId, acceptedFiles);
      }
    },
    [poId, uploadInvoices]
  );

  const handleProcessInvoices = useCallback(() => {
    if (poId) {
      processInvoices(poId);
    }
  }, [poId, processInvoices]);

  const summary = useMemo(() => {
    if (!po?.lineItems) return { totalItems: 0, totalUnits: 0, poAmount: 0, receivedAmount: 0, shortShipped: 0, outOfStockPct: 0 };

    let totalItems = 0;
    let totalUnits = 0;
    let poAmount = 0;
    let receivedAmount = 0;
    let shortShippedCount = 0;

    for (const li of po.lineItems) {
      const row = rows.get(li.id);
      const qtyRecv = parseInt(row?.quantityReceived || "0");
      const outstanding = li.quantityOrdered - li.quantityReceived;
      const isChecked = row?.received ?? false;

      poAmount += Number(li.netCaseCost) * li.quantityOrdered;

      if (isChecked && qtyRecv > 0) {
        totalItems++;
        totalUnits += qtyRecv * li.packSize;
        receivedAmount += Number(li.netCaseCost) * qtyRecv;
      }

      if (!isChecked && outstanding > 0) {
        shortShippedCount++;
      } else if (isChecked && qtyRecv < outstanding) {
        shortShippedCount++;
      }
    }

    const shortShipped = Math.max(0, poAmount - receivedAmount);
    const outOfStockPct = po.lineItems.length > 0 ? (shortShippedCount / po.lineItems.length) * 100 : 0;

    return { totalItems, totalUnits, poAmount, receivedAmount, shortShipped, outOfStockPct };
  }, [po, rows]);

  const handleConfirmReceive = useCallback(async () => {
    if (!poId) return;
    setConfirmOpen(false);
    setSaving(true);

    const lineItems = Array.from(rows.values())
      .filter((r) => r.received && r.poLineItemId && parseInt(r.quantityReceived) > 0)
      .map((r) => ({
        poLineItemId: r.poLineItemId!,
        quantityReceived: parseInt(r.quantityReceived),
        quantityDamaged: parseInt(r.quantityDamaged) || 0,
        invoicedCaseCost: r.invoicedCaseCost
          ? parseFloat(r.invoicedCaseCost)
          : null,
        newRetailPrice: r.newRetailPrice
          ? parseFloat(r.newRetailPrice)
          : null,
        printLabel: r.printLabel,
        notes: r.notes || undefined,
      }));

    const result = await receivePurchaseOrder(poId, {
      invoiceNumber: invoiceNumber || undefined,
      notes: receivingNotes || undefined,
      lineItems,
    });

    setSaving(false);

    if (result.data) {
      if (!result.shopifySynced && result.shopifyErrors?.length) {
        setShopifyWarning(
          `Receiving saved but Shopify inventory sync failed: ${result.shopifyErrors.map((e) => e.message).join(", ")}`
        );
      }
      setToastMessage("Shipment received successfully");
      setTimeout(() => navigate(`/purchase-orders/${poId}`), 1500);
    }
  }, [poId, rows, invoiceNumber, receivingNotes, receivePurchaseOrder, navigate]);

  const error = poError || invoiceError;

  if (loading || !po) {
    return (
      <Page title="Loading...">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ textAlign: "center", padding: "40px" }}>
                <Spinner size="large" />
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // Get blue (new) items from process result
  const blueItems = processResult?.matchedItems.filter((i) => i.classification === "blue") || [];

  return (
    <>
      <Page
        title={`Receive Shipment — ${po.poNumber}`}
        backAction={{ content: "Back", url: `/purchase-orders/${poId}` }}
        primaryAction={{
          content: "Receive Purchase Order",
          onAction: () => setConfirmOpen(true),
          loading: saving,
          disabled: summary.totalItems === 0,
        }}
      >
        {error && (
          <Layout.Section>
            <Banner tone="critical">
              <p>{error}</p>
            </Banner>
          </Layout.Section>
        )}

        {shopifyWarning && (
          <Layout.Section>
            <Banner tone="warning" onDismiss={() => setShopifyWarning(null)}>
              <p>{shopifyWarning}</p>
            </Banner>
          </Layout.Section>
        )}

        <Layout>
          {/* Compact Header — 4-column grid */}
          <Layout.Section>
            <Card>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "12px", alignItems: "end" }}>
                {/* Row 1: Vendor, PO Date, Invoice #, Invoice Date */}
                <div>
                  <Text as="span" variant="bodySm" tone="subdued">Vendor</Text>
                  <Text as="p" fontWeight="bold">{po.vendorName || po.vendor?.name || "—"}</Text>
                </div>
                <div>
                  <Text as="span" variant="bodySm" tone="subdued">PO Date</Text>
                  <Text as="p">{po.orderDate ? new Date(po.orderDate).toLocaleDateString() : "—"}</Text>
                </div>
                <div>
                  <TextField
                    label="Invoice #"
                    value={invoiceNumber}
                    onChange={setInvoiceNumber}
                    placeholder="Auto-filled from invoice"
                    autoComplete="off"
                    size="slim"
                  />
                </div>
                <div>
                  <Text as="span" variant="bodySm" tone="subdued">Invoice Date</Text>
                  <Text as="p">{invoiceDate || "—"}</Text>
                </div>

                {/* Row 2: PO Amount, Received Amount, Short Shipped, Out of Stock % */}
                <div>
                  <Text as="span" variant="bodySm" tone="subdued">PO Amount</Text>
                  <Text as="p" fontWeight="bold">{formatCurrency(summary.poAmount)}</Text>
                </div>
                <div>
                  <Text as="span" variant="bodySm" tone="subdued">Received Amount</Text>
                  <Text as="p" fontWeight="bold">{formatCurrency(summary.receivedAmount)}</Text>
                </div>
                <div>
                  <Text as="span" variant="bodySm" tone="subdued">Short Shipped</Text>
                  <Text as="p" fontWeight="bold" tone={summary.shortShipped > 0 ? "critical" : undefined}>
                    {formatCurrency(summary.shortShipped)}
                  </Text>
                </div>
                <div>
                  <Text as="span" variant="bodySm" tone="subdued">Out of Stock %</Text>
                  <Text as="p" fontWeight="bold" tone={summary.outOfStockPct > 0 ? "critical" : undefined}>
                    {summary.outOfStockPct.toFixed(1)}%
                  </Text>
                </div>

                {/* Row 3: Notes spanning full width */}
                <div style={{ gridColumn: "1 / -1" }}>
                  <TextField
                    label="Notes"
                    value={receivingNotes}
                    onChange={setReceivingNotes}
                    placeholder="Optional receiving notes..."
                    autoComplete="off"
                    size="slim"
                  />
                </div>
              </div>
            </Card>
          </Layout.Section>

          {/* Invoice Upload Section */}
          <Layout.Section>
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">Invoice PDFs</Text>

                <DropZone
                  accept="application/pdf"
                  type="file"
                  onDrop={handleDropZoneDrop}
                  allowMultiple
                  variableHeight
                >
                  <DropZone.FileUpload actionHint="or drop PDF files to upload" />
                </DropZone>

                {uploading && (
                  <InlineStack gap="200" blockAlign="center">
                    <Spinner size="small" />
                    <Text as="span">Uploading...</Text>
                  </InlineStack>
                )}

                {uploadedInvoices.length > 0 && (
                  <BlockStack gap="200">
                    {uploadedInvoices.map((inv) => (
                      <InlineStack key={inv.id} gap="200" align="space-between" blockAlign="center">
                        <InlineStack gap="200" blockAlign="center">
                          <Text as="span" variant="bodySm">{inv.filename}</Text>
                          <Badge tone={
                            inv.status === "parsed" ? "success" :
                            inv.status === "parsing" ? "attention" :
                            inv.status === "error" ? "critical" : undefined
                          }>
                            {inv.status}
                          </Badge>
                          {inv.invoiceNumber && (
                            <Text as="span" variant="bodySm" tone="subdued">#{inv.invoiceNumber}</Text>
                          )}
                        </InlineStack>
                        <Button
                          icon={DeleteIcon}
                          variant="plain"
                          tone="critical"
                          onClick={() => poId && deleteInvoice(poId, inv.id)}
                          accessibilityLabel="Delete invoice"
                        />
                      </InlineStack>
                    ))}
                  </BlockStack>
                )}

                {uploadedInvoices.length > 0 && (
                  <InlineStack gap="200" blockAlign="center">
                    {!hasSavedResults ? (
                      <Button
                        variant="primary"
                        onClick={handleProcessInvoices}
                        loading={processing}
                        disabled={uploadedInvoices.length === 0}
                      >
                        {processing ? "Processing with Claude..." : "Process Invoices"}
                      </Button>
                    ) : (
                      <Button
                        onClick={async () => {
                          if (poId) {
                            await clearSavedResults(poId);
                            setInvoiceProcessed(false);
                            // Reset rows to default
                            if (po?.lineItems) {
                              const map = new Map<string, ReceivingRow>();
                              for (const li of po.lineItems) {
                                const outstanding = li.quantityOrdered - li.quantityReceived;
                                map.set(li.id, {
                                  poLineItemId: li.id,
                                  received: outstanding > 0,
                                  quantityReceived: outstanding > 0 ? String(outstanding) : "0",
                                  quantityDamaged: "0",
                                  invoicedCaseCost: "",
                                  newRetailPrice: li.retailPrice ? String(Number(li.retailPrice)) : "",
                                  printLabel: false,
                                  notes: "",
                                  classification: null,
                                });
                              }
                              setRows(map);
                            }
                            setInvoiceNumber("");
                            setInvoiceDate("");
                          }
                        }}
                        tone="critical"
                      >
                        Clear Results & Re-process
                      </Button>
                    )}
                    {invoiceProcessed && (
                      <Badge tone="success">Invoices processed — review below</Badge>
                    )}
                  </InlineStack>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>

          {/* Legend (show after processing) */}
          {invoiceProcessed && processResult && (
            <Layout.Section>
              <InlineStack gap="300" wrap>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 16, height: 16, background: ROW_COLORS.green.bg, border: "1px solid #ccc", borderRadius: 3 }} />
                  <Text as="span" variant="bodySm">Received OK ({processResult.summary.greenCount})</Text>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 16, height: 16, background: ROW_COLORS.yellow.bg, border: "1px solid #ccc", borderRadius: 3 }} />
                  <Text as="span" variant="bodySm">Cost Change ({processResult.summary.yellowCount})</Text>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 16, height: 16, background: ROW_COLORS.red.bg, border: "1px solid #ccc", borderRadius: 3 }} />
                  <Text as="span" variant="bodySm">Short Shipped ({processResult.summary.redCount})</Text>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 16, height: 16, background: ROW_COLORS.blue.bg, border: "1px solid #ccc", borderRadius: 3 }} />
                  <Text as="span" variant="bodySm">New Product ({processResult.summary.blueCount})</Text>
                </div>
              </InlineStack>
            </Layout.Section>
          )}

          {/* Check All / Uncheck All */}
          <Layout.Section>
            <InlineStack gap="200">
              <Button onClick={handleCheckAll}>Check All</Button>
              <Button onClick={handleUncheckAll}>Uncheck All</Button>
            </InlineStack>
          </Layout.Section>

          {/* Receiving List */}
          <Layout.Section>
            <Card padding="0">
              <style>{`
                .recv-item { border-bottom: 3px solid #e1e3e5; }
                .recv-item:last-child { border-bottom: none; }
                .recv-item.unchecked { opacity: 0.35; }
                .recv-line1 { display: flex; align-items: center; padding: 10px 14px 6px; gap: 10px; background: rgba(0,0,0,0.03); border-bottom: 1px solid rgba(0,0,0,0.06); }
                .recv-line2 { display: flex; align-items: center; padding: 8px 14px 10px; gap: 0; flex-wrap: wrap; }
                .recv-img { width: 44px; height: 44px; border-radius: 6px; object-fit: cover; background: #f1f1f1; flex-shrink: 0; border: 1px solid #e1e3e5; }
                .recv-img-placeholder { width: 44px; height: 44px; border-radius: 6px; background: #f1f1f1; flex-shrink: 0; border: 1px solid #e1e3e5; display: flex; align-items: center; justify-content: center; color: #c0c0c0; font-size: 18px; }
                .recv-prod { flex: 1; min-width: 0; }
                .recv-prod-name { font-weight: 600; font-size: 14px; line-height: 1.3; }
                .recv-prod-sub { font-size: 11px; color: #6d7175; margin-top: 1px; }
                .recv-qty { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
                .recv-qty-label { font-size: 11px; color: #6d7175; font-weight: 500; }
                .recv-qty-val { font-size: 15px; font-weight: 700; min-width: 20px; text-align: right; }
                .recv-qty-input { width: 65px; flex-shrink: 0; }
                .recv-qty-input .Polaris-TextField { min-height: 32px; }
                .recv-qty-input .Polaris-TextField__Input { padding: 4px 6px; font-size: 13px; text-align: right; }
                .rv { display: flex; flex-direction: column; align-items: flex-end; padding: 0 12px; }
                .rv-label { font-size: 10px; color: #8c9196; text-transform: uppercase; letter-spacing: 0.3px; line-height: 1; margin-bottom: 3px; }
                .rv-val { font-size: 14px; font-weight: 600; white-space: nowrap; }
                .rv-sep { width: 1px; height: 30px; background: #d1d5db; margin: 0 8px; flex-shrink: 0; }
                .rv-input { width: 130px; display: flex; flex-direction: column; flex-shrink: 0; }
                .rv-input .Polaris-TextField { min-height: 32px; }
                .rv-input .Polaris-TextField__Input { padding: 4px 6px; font-size: 13px; text-align: right; }
                .rv-lbl { display: flex; align-items: center; padding-left: 10px; }
                .rv-spacer { flex: 1; }
              `}</style>

              {(po.lineItems || []).map((li) => {
                const row = rows.get(li.id);
                const outstanding = li.quantityOrdered - li.quantityReceived;
                const isChecked = row?.received ?? false;
                const classification = row?.classification;
                const rowBg = classification ? ROW_COLORS[classification].bg : undefined;
                const productLabel = (li.productTitle || "") +
                  (li.variantTitle && li.variantTitle !== li.productTitle ? ` — ${li.variantTitle}` : "");
                const displayCaseCost = row?.invoicedCaseCost && classification === "yellow"
                  ? parseFloat(row.invoicedCaseCost)
                  : Number(li.caseCost);

                const currentPrice = Number(li.retailPrice) || 0;
                const newPrice = parseFloat(row?.newRetailPrice || "0");
                const unitCost = Number(li.packSize) > 0 ? Number(li.netCaseCost) / Number(li.packSize) : 0;
                const margin = newPrice > 0 ? ((newPrice - unitCost) / newPrice) * 100 : 0;
                const marginColor = newPrice > 0 ? (margin < 30 ? "#c41e3a" : margin < 40 ? "#b8860b" : "#2e7d32") : "#999";

                return (
                  <div key={li.id} className={`recv-item ${isChecked ? "" : "unchecked"}`} style={rowBg ? { backgroundColor: rowBg } : undefined}>
                    {/* Line 1: Checkbox | Image | Product Name | Qty | Recv input */}
                    <div className="recv-line1" style={rowBg ? { background: `linear-gradient(rgba(0,0,0,0.04), rgba(0,0,0,0.04)), ${rowBg}` } : undefined}>
                      <Checkbox label="" labelHidden checked={isChecked} onChange={() => handleToggleReceived(li.id, outstanding)} />
                      {li.imageUrl ? (
                        <img src={li.imageUrl} alt="" className="recv-img" />
                      ) : (
                        <div className="recv-img-placeholder">?</div>
                      )}
                      <div className="recv-prod">
                        <div className="recv-prod-name" title={productLabel}>{productLabel || "—"}</div>
                        <div className="recv-prod-sub">{li.vendorProductId || li.sku || "—"}</div>
                      </div>
                      <div className="recv-qty">
                        <span className="recv-qty-label">Ordered</span>
                        <span className="recv-qty-val">{li.quantityOrdered}</span>
                      </div>
                      <div className="recv-qty-input">
                        <TextField label="Recv" value={row?.quantityReceived || "0"} onChange={(v) => handleRowChange(li.id, "quantityReceived", v)} type="number" min={0} max={outstanding} autoComplete="off" disabled={!isChecked} />
                      </div>
                    </div>

                    {/* Line 2: Case Cost | Vend% | Off Inv | Net Case || Price | New Price | Margin || Label */}
                    <div className="recv-line2">
                      <div className="rv">
                        <span className="rv-label">Case Cost</span>
                        <span className="rv-val" style={classification === "yellow" ? { color: "#b8860b" } : undefined}>{formatCurrency(displayCaseCost)}</span>
                      </div>
                      {Number(li.vendorDiscountPct) > 0 && (
                        <div className="rv">
                          <span className="rv-label">Vend %</span>
                          <span className="rv-val">{Number(li.vendorDiscountPct)}%</span>
                        </div>
                      )}
                      {Number(li.offInvoice) > 0 && (
                        <div className="rv">
                          <span className="rv-label">Off Inv</span>
                          <span className="rv-val">{formatCurrency(Number(li.offInvoice))}</span>
                        </div>
                      )}
                      <div className="rv">
                        <span className="rv-label">Net Case</span>
                        <span className="rv-val">{formatCurrency(Number(li.netCaseCost))}</span>
                      </div>

                      <div className="rv-sep" />

                      <div className="rv">
                        <span className="rv-label">Current</span>
                        <span className="rv-val" style={{ color: "#6d7175", fontWeight: 400 }}>{currentPrice > 0 ? formatCurrency(currentPrice) : "—"}</span>
                      </div>
                      <div className="rv-input">
                        <span className="rv-label" style={{ fontSize: 10, color: "#8c9196", textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 2 }}>New Price</span>
                        <TextField label="" labelHidden type="number" value={row?.newRetailPrice || ""} onChange={(v) => handleRowChange(li.id, "newRetailPrice", v)} prefix="$" autoComplete="off" disabled={!isChecked} />
                      </div>
                      <div className="rv">
                        <span className="rv-label">Margin</span>
                        <span className="rv-val" style={{ color: marginColor }}>{newPrice > 0 ? `${margin.toFixed(1)}%` : "—"}</span>
                      </div>

                      <div className="rv-sep" />

                      <div className="rv-lbl">
                        <Checkbox label="Print Label" checked={row?.printLabel || false} onChange={(v) => handleRowChange(li.id, "printLabel", String(v))} disabled={!isChecked} />
                      </div>
                      <div className="rv-spacer" />
                    </div>
                  </div>
                );
              })}

              {/* Blue rows: new products from invoice not on PO */}
              {blueItems.map((item, idx) => (
                <div key={`blue-${idx}`} className="recv-item" style={{ backgroundColor: ROW_COLORS.blue.bg }}>
                  <div className="recv-line1" style={{ background: `linear-gradient(rgba(0,0,0,0.04), rgba(0,0,0,0.04)), ${ROW_COLORS.blue.bg}` }}>
                    <Badge tone="info">NEW</Badge>
                    <div className="recv-img-placeholder">?</div>
                    <div className="recv-prod">
                      <div className="recv-prod-name">{item.invoiceDescription}</div>
                      <div className="recv-prod-sub">{item.invoiceVendorCode}</div>
                    </div>
                    <div className="recv-qty">
                      <span className="recv-qty-label">Qty</span>
                      <span className="recv-qty-val">{item.invoiceQty}</span>
                    </div>
                  </div>
                  <div className="recv-line2">
                    <div className="rv">
                      <span className="rv-label">Case Cost</span>
                      <span className="rv-val">{item.invoiceCaseCost != null ? formatCurrency(item.invoiceCaseCost) : "—"}</span>
                    </div>
                  </div>
                </div>
              ))}
            </Card>
          </Layout.Section>
        </Layout>

        {/* Confirmation Modal */}
        <Modal
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          title="Confirm Receiving"
          primaryAction={{
            content: "Receive Purchase Order",
            onAction: handleConfirmReceive,
            loading: saving,
          }}
          secondaryActions={[
            { content: "Cancel", onAction: () => setConfirmOpen(false) },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="200">
              <Text as="p">
                This will receive {summary.totalItems} item(s) ({summary.totalUnits} units)
                and update Shopify inventory for {po.poNumber}.
              </Text>
              <InlineStack gap="400">
                <BlockStack gap="100">
                  <Text as="span" tone="subdued">PO Amount</Text>
                  <Text as="span" fontWeight="bold">{formatCurrency(summary.poAmount)}</Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text as="span" tone="subdued">Received</Text>
                  <Text as="span" fontWeight="bold">{formatCurrency(summary.receivedAmount)}</Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text as="span" tone="subdued">Short Shipped</Text>
                  <Text as="span" fontWeight="bold" tone="critical">{formatCurrency(summary.shortShipped)}</Text>
                </BlockStack>
              </InlineStack>
              {invoiceNumber && (
                <Text as="p" tone="subdued">Invoice: {invoiceNumber}</Text>
              )}
              <Text as="p" tone="subdued">
                Shopify inventory will be adjusted by the received quantities.
                This action cannot be undone.
              </Text>
            </BlockStack>
          </Modal.Section>
        </Modal>

        {toastMessage && (
          <Toast content={toastMessage} onDismiss={() => setToastMessage(null)} />
        )}
      </Page>
    </>
  );
}
